import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, Field, Input } from '@/design-system';
import { authApi, authProblem, isRevokedDevice } from '@/entities/auth';
import { detectPlatform, isFieldPlatform, isApiError, useSessionStore } from '@/shared/api';
import { WEBSITE_SIGNUP_URL } from '@/shared/lib';

import {
    AuthError,
    AuthExternalLink,
    AuthHeading,
    AuthLayout,
    AuthLink,
    AuthNotice,
} from './AuthLayout';
import { authCopy } from './copy';
import { tenantSwitchError, useLogout, useTenantSwitch } from './hooks';
import { TenantPicker } from './TenantPicker';
import { validateEmail } from './validation';

interface FieldErrors {
    email?: string;
    password?: string;
}

interface FormError {
    message: string;
    tone: 'danger' | 'offline';
}

/**
 * Inloggen (scherm 1).
 *
 * `POST /auth/login` levert meteen een volledige sessie. Kiest de API zelf een
 * omgeving (één lidmaatschap, of één als standaard), dan stuurt de routerguard
 * je door. Werk je voor meerdere bedrijven zonder standaard, dan blijft dit
 * scherm staan en wordt het de omgevingskeuze - dezelfde sessie, nog zonder
 * omgeving. De keuze wordt bij de API onthouden (`remember`), zodat hij de
 * volgende keer meteen opent.
 */
export function LoginPage() {
    const session = useSessionStore((state) => state.session);

    return (
        <AuthLayout>
            {session && session.tenant === null ? <ChooseWorkspace /> : <LoginForm />}
        </AuthLayout>
    );
}

function LoginForm() {
    const signIn = useSessionStore((state) => state.signIn);
    const endedBecause = useSessionStore((state) => state.endedBecause);
    const [search] = useSearchParams();

    const [email, setEmail] = useState(search.get('email') ?? '');
    const [password, setPassword] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [formError, setFormError] = useState<FormError | null>(null);
    const [busy, setBusy] = useState(false);

    /*
     * `device-unavailable` als reden betekent: ingetrokken. Verlengen met een
     * apparaat dat niet meer vertrouwd is, eindigt als `expired` - daar helpt
     * opnieuw inloggen wel (zie `session-lifecycle`).
     */
    const notice =
        endedBecause === 'expired'
            ? authCopy.sessionExpired
            : endedBecause === 'device-unavailable'
              ? authCopy.deviceRevokedNotice
              : null;

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();

        const errors: FieldErrors = {
            ...(validateEmail(email) ? { email: validateEmail(email) } : {}),
            ...(password === '' ? { password: authCopy.passwordRequired } : {}),
        };

        setFieldErrors(errors);
        setFormError(null);

        if (errors.email || errors.password) {
            return;
        }

        setBusy(true);

        try {
            await signIn(await authApi.login({ email, password }));
        } catch (caught) {
            const fields = apiFieldErrors(caught);

            setBusy(false);
            setPassword('');
            setFieldErrors(fields);
            setFormError(fields.email || fields.password ? null : loginError(caught));
        }
    }

    return (
        <>
            <AuthHeading
                title={authCopy.loginTitle}
                body={
                    isFieldPlatform(detectPlatform())
                        ? authCopy.deviceNoticeField
                        : authCopy.deviceNoticeOffice
                }
            />

            {notice ? <AuthNotice>{notice}</AuthNotice> : null}

            <form noValidate className="flex flex-col gap-3" onSubmit={handleSubmit}>
                <Field label={authCopy.email} labelCase="caps" size="lg" error={fieldErrors.email}>
                    <Input
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </Field>

                <Field
                    label={authCopy.password}
                    labelCase="caps"
                    size="lg"
                    error={fieldErrors.password}
                >
                    <Input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                </Field>

                {formError ? (
                    <AuthError tone={formError.tone}>{formError.message}</AuthError>
                ) : null}

                <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
                    {busy ? authCopy.submitBusy : authCopy.submit}
                </Button>

                <div className="flex justify-start">
                    <AuthLink
                        to={
                            email.trim() === ''
                                ? paths.forgotPassword
                                : `${paths.forgotPassword}?email=${encodeURIComponent(email.trim())}`
                        }
                    >
                        {authCopy.forgotLink}
                    </AuthLink>
                </div>
            </form>

            {/* Wie hier zonder account staat, probeert Smartkeur via de website. */}
            <div className="flex justify-start border-t border-[var(--border-default)] pt-4">
                <AuthExternalLink href={WEBSITE_SIGNUP_URL}>{authCopy.signupLink}</AuthExternalLink>
            </div>
        </>
    );
}

/**
 * Ingelogd, maar nog zonder omgeving: kiezen (of, zonder lidmaatschappen,
 * uitleggen dat er niets te kiezen valt).
 */
function ChooseWorkspace() {
    const session = useSessionStore((state) => state.session);
    const switcher = useTenantSwitch();
    const logout = useLogout();

    const memberships = [...(session?.memberships ?? [])].sort((left, right) =>
        left.isDefault === right.isDefault
            ? left.tenant.name.localeCompare(right.tenant.name, 'nl')
            : left.isDefault
              ? -1
              : 1,
    );

    if (memberships.length === 0) {
        return (
            <>
                <AuthHeading title={authCopy.noWorkspaceTitle} body={authCopy.noWorkspaceBody} />
                <Button variant="outline" size="lg" fullWidth onClick={() => void logout()}>
                    {authCopy.signOut}
                </Button>
            </>
        );
    }

    return (
        <>
            <TenantPicker
                memberships={memberships}
                busyTenantUuid={
                    switcher.isPending ? (switcher.variables?.tenant.uuid ?? null) : null
                }
                error={switcher.isError ? tenantSwitchError(switcher.error) : null}
                onPick={(membership) =>
                    switcher.mutate({ tenant: membership.tenant, remember: true })
                }
            />
            <div className="flex justify-start">
                <Button variant="ghost" onClick={() => void logout()}>
                    {authCopy.signOut}
                </Button>
            </div>
        </>
    );
}

/** Zegt nooit of het account bestaat. */
function loginError(error: unknown): FormError {
    switch (authProblem(error)) {
        case 'invalid-credentials':
        case 'unauthenticated':
            return { message: authCopy.credentialsWrong, tone: 'danger' };
        case 'rate-limited': {
            const seconds = isApiError(error) ? Math.ceil((error.retryAfterMs ?? 0) / 1_000) : 0;

            return { message: authCopy.rateLimited(seconds), tone: 'danger' };
        }
        case 'device-unavailable':
            return {
                message: isRevokedDevice(error)
                    ? authCopy.deviceRevoked
                    : authCopy.deviceUnavailable,
                tone: 'danger',
            };
        case 'offline':
            return { message: authCopy.offline, tone: 'offline' };
        default:
            return { message: authCopy.loginFailed, tone: 'danger' };
    }
}

/** Veldfouten uit een 422, alleen voor de velden op dit formulier. */
function apiFieldErrors(error: unknown): FieldErrors {
    if (!isApiError(error) || authProblem(error) !== 'validation') {
        return {};
    }

    const email = error.firstValidationError('email');
    const password = error.firstValidationError('password');

    return { ...(email ? { email } : {}), ...(password ? { password } : {}) };
}
